import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { ReportCategory, TermPeriod, ContractScope } from "@/types/reports"

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

/**
 * Get initials from a name string (first + last initial)
 */
export function getInitials(name: string): string {
	if (!name) return "U"
	const parts = name.trim().split(/\s+/)
	if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/**
 * Format a number as a Euro currency string
 * @param amount - The amount to format
 * @param locale - The locale to use for formatting (default: 'es-ES' for Euro formatting)
 * @returns Formatted currency string with Euro symbol
 */
export function formatEuro(amount: number, locale: string = "es-ES"): string {
	return new Intl.NumberFormat(locale, {
		style: "currency",
		currency: "EUR",
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount)
}

/**
 * Format a number as a Euro currency string without the currency symbol
 * @param amount - The amount to format
 * @param locale - The locale to use for formatting (default: 'es-ES')
 * @returns Formatted number string
 */
export function formatEuroNumber(amount: number, locale: string = "es-ES"): string {
	return new Intl.NumberFormat(locale, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount)
}

/**
 * Get the maximum allowed budget for minor contracts in a category.
 * Based on LCSP thresholds: obres < 40,000€, serveis/subministraments < 15,000€
 */
export function getMaxBudgetForCategory(category: ReportCategory): number {
	switch (category) {
		case ReportCategory.OBRES:
			return 39999.99
		default:
			return 14999.99
	}
}

/**
 * Detect contract scope based on budget and category.
 * Uses minor contract thresholds from LCSP art. 118.
 */
export function detectContractScope(budget: number, category: ReportCategory): ContractScope {
	const maxAllowed = getMaxBudgetForCategory(category)
	return budget > maxAllowed ? ContractScope.NON_MINOR : ContractScope.MINOR
}

/**
 * Validate and adjust budget if it exceeds category limits
 * @param budget - The proposed budget amount
 * @param category - The report category
 * @param contractScope - Optional scope; when NON_MINOR, skip the budget cap
 * @returns Object with validated budget and whether it was adjusted
 */
export function validateBudgetForCategory(
	budget: number,
	category: ReportCategory,
	contractScope?: ContractScope,
): {
	validatedBudget: number
	wasAdjusted: boolean
	maxAllowed: number
} {
	const maxAllowed = getMaxBudgetForCategory(category)

	// Skip budget cap for non-minor contracts
	if (contractScope === ContractScope.NON_MINOR) {
		return {
			validatedBudget: budget,
			wasAdjusted: false,
			maxAllowed,
		}
	}

	const wasAdjusted = budget > maxAllowed
	const validatedBudget = wasAdjusted ? maxAllowed : budget

	return {
		validatedBudget,
		wasAdjusted,
		maxAllowed,
	}
}

/**
 * Convert a term period to days for comparison
 * @param value - The numeric value
 * @param period - The period type (dies/setmanes/mesos)
 * @returns Number of days, or null if invalid
 */
function convertPeriodToDays(value: number, period: TermPeriod): number | null {
	if (!value || value <= 0) return null

	switch (period) {
		case TermPeriod.DIES:
			return value
		case TermPeriod.SETMANES:
			return value * 7
		case TermPeriod.MESOS:
			return value * 30 // Approximate 30 days per month
		default:
			return null
	}
}

/**
 * Validate that the term period does not exceed 12 months (365 days)
 * @param termValue - The numeric value
 * @param termPeriod - The period type
 * @returns Object with validation result and details
 */
export function validateTermPeriod(
	termValue?: number,
	termPeriod?: TermPeriod,
	contractScope?: ContractScope,
): {
	isValid: boolean
	exceedsLimit: boolean
	totalDays: number | null
	maxAllowedDays: number
	maxAllowedMonths: number
} {
	const maxAllowedDays = 365 // 12 months * ~30.4 days
	const maxAllowedMonths = 12

	// Non-minor contracts have no term limit
	if (contractScope === ContractScope.NON_MINOR) {
		const totalDays = termValue && termPeriod ? convertPeriodToDays(termValue, termPeriod) : null
		return {
			isValid: true,
			exceedsLimit: false,
			totalDays,
			maxAllowedDays,
			maxAllowedMonths,
		}
	}

	if (!termValue || !termPeriod) {
		return {
			isValid: true, // Allow empty values
			exceedsLimit: false,
			totalDays: null,
			maxAllowedDays,
			maxAllowedMonths,
		}
	}

	const totalDays = convertPeriodToDays(termValue, termPeriod)

	if (totalDays === null) {
		return {
			isValid: false,
			exceedsLimit: false,
			totalDays: null,
			maxAllowedDays,
			maxAllowedMonths,
		}
	}

	const exceedsLimit = totalDays > maxAllowedDays

	return {
		isValid: !exceedsLimit,
		exceedsLimit,
		totalDays,
		maxAllowedDays,
		maxAllowedMonths,
	}
}
